/* eslint camelcase: "off" */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from '@jem-open/jem-ui';
import { AlertTriangle, Loader2 } from 'lucide-react';

import apiResponseHandler from '../../../../utils/apiResponseHandler';
import { PersonAggregate, verificationClient } from '../../../../services/verificationClient';
import {
  AllowedActions,
  CurrentEmployeeCredential,
  EmployeeCredential,
} from '../../../../types/VerificationTypes';
import { severityTagVariant } from '../../Security/components/identityVerifications/severity';


type EventVariant = 'success' | 'failed' | 'drafted';
const EVENT_COLOR: Record<string, EventVariant> = {
  granted: 'success',
  revoked: 'failed',
  expired: 'drafted',
};


function formatTimestamp(iso: string) {
  return new Date(iso).toLocaleString();
}


// ---------------------------------------------------------------------------
// Allowed actions panel — shows what this employee can do right now per the
// verification gate, grouped by decision. Same endpoint chatbot uses to
// render its menu; exposed here for support triage.
// ---------------------------------------------------------------------------

const AllowedActionsPanel: React.FC<{ employeeId: number }> = ({ employeeId }) => {
  const [data, setData] = useState<AllowedActions | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await verificationClient.getAllowedActions(employeeId));
    } catch (err) {
      apiResponseHandler.handleApiError(err);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && data == null) {
    return (
      <div className="flex justify-center p-md">
        <Loader2 className="animate-spin text-secondary-text-label" size={20} />
      </div>
    );
  }
  if (data == null) return null;

  const renderBucket = (
    label: string,
    hint: string,
    items: AllowedActions['ready'],
    chipVariant: 'success' | 'processing' | 'pending' | 'failed'
  ) => {
    if (items.length === 0) return null;
    return (
      <div className="mb-sm">
        <div className="mb-xs flex items-baseline gap-xxs">
          <span className="text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption">
            {label}
          </span>
          <span className="text-xs text-greyscale-text-caption">·</span>
          <span className="text-xs italic text-greyscale-text-caption">{hint}</span>
        </div>
        <div className="flex flex-wrap gap-xxs">
          {items.map((d) => (
            <Tag key={d.action_code} variant={chipVariant}>
              {d.action_code}
            </Tag>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      {renderBucket('Ready', 'allowed right now', data.ready, 'success')}
      {renderBucket(
        'Remediable',
        'chatbot can capture the missing credentials in-band',
        data.remediable,
        'processing'
      )}
      {renderBucket(
        'Step-up',
        'employee needs to re-verify a fresh factor',
        data.step_up,
        'pending'
      )}
      {renderBucket(
        'Denied',
        'hard-blocked, chatbot will hide these from the menu',
        data.denied,
        'failed'
      )}
      {data.ready.length === 0 &&
        data.remediable.length === 0 &&
        data.step_up.length === 0 &&
        data.denied.length === 0 && (
          <p className="m-none text-sm italic text-greyscale-text-caption">
            No actions evaluated — registry may be empty.
          </p>
        )}
      <p className="m-none mt-xs text-xs text-greyscale-text-caption">
        Policy version hash: <code className="font-mono">{data.policy_version_hash}</code>
      </p>
    </div>
  );
};


interface EmployeeVerificationSectionProps {
  employeeId: number;
}


/**
 * Per-employee verification view embedded in the EmployeeDetail page.
 *
 * Renders two blocks:
 *   - Current credential state (materialized)
 *   - Ledger history (filterable)
 *
 * No PageHeader / no routing concerns — designed to slot in as the content
 * of a tab on EmployeeDetail.js.
 */
const EmployeeVerificationSection: React.FC<EmployeeVerificationSectionProps> = ({
  employeeId,
}) => {
  const [current, setCurrent] = useState<CurrentEmployeeCredential[]>([]);
  const [ledger, setLedger] = useState<EmployeeCredential[]>([]);
  const [requirementFilter, setRequirementFilter] = useState('');
  // Debounce the typed filter so we don't fire a fetch on every keystroke.
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSnapshot, setShowSnapshot] = useState<string | null>(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedFilter(requirementFilter), 350);
    return () => clearTimeout(timeoutId);
  }, [requirementFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cur, hist] = await Promise.all([
        verificationClient.getCurrentEmployeeCredentials(employeeId),
        verificationClient.listEmployeeCredentials(employeeId, debouncedFilter || undefined),
      ]);
      setCurrent(cur);
      setLedger(hist);
    } catch (err) {
      apiResponseHandler.handleApiError(err);
    } finally {
      setLoading(false);
    }
  }, [employeeId, debouncedFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Fraud-review banner — risk score, open signals, BSUID/MSISDN from
  // the per-employee aggregate. Suppressed entirely when the API
  // returns null (no verification activity in the rollup window).
  const [person, setPerson] = useState<PersonAggregate | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPerson(null);
    verificationClient
      .getPerson(employeeId)
      .then((p) => {
        if (cancelled) return;
        setPerson(p);
      })
      .catch(() => {
        if (cancelled) return;
        setPerson(null);
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  return (
    <div>
      {person && (
        <div className="mb-md rounded-2xl border border-greyscale-border-default bg-white p-md">
          <div className="flex flex-wrap items-start justify-between gap-sm">
            <div>
              {person.employer_name && (
                <div className="text-xs text-greyscale-text-caption">
                  {person.employer_name}
                </div>
              )}
              <div className="mt-xxs flex flex-wrap items-center gap-md text-xs text-greyscale-text-caption">
                {person.bsuid && (
                  <span>BSUID: <code>{person.bsuid}</code></span>
                )}
                {person.msisdn && (
                  <span>MSISDN: <code>{person.msisdn}</code></span>
                )}
                <span>Last seen: {new Date(person.last_seen).toLocaleString()}</span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-xxs">
              <div className="text-xs uppercase tracking-wide text-greyscale-text-caption">Risk</div>
              <Tag variant={severityTagVariant(person.risk_score)}>{person.risk_score}</Tag>
            </div>
          </div>

          {person.open_signals.length > 0 && (
            <div className="mt-sm flex flex-wrap items-center gap-xxs">
              <AlertTriangle size={14} className="text-error-text-label" />
              <span className="text-xs font-semibold text-error-text-label">Open signals:</span>
              {person.open_signals.map((s) => (
                <Tag key={s} variant="failed">
                  {s}
                </Tag>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Allowed actions — what this employee can do right now */}
      <div className="mb-md rounded-2xl border border-greyscale-border-default bg-white p-md">
        <h4 className="mb-sm text-sm font-semibold">
          Allowed actions
          <span className="ml-xxs text-xs font-normal text-greyscale-text-caption">
            — verification gate&apos;s view of what this employee can do right now
          </span>
        </h4>
        <AllowedActionsPanel employeeId={employeeId} />
      </div>

      <div className="grid grid-cols-1 gap-md lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="rounded-2xl border border-greyscale-border-default bg-white p-md">
            <h4 className="mb-sm text-sm font-semibold">
              Current State
              <span className="ml-xxs text-xs font-normal text-greyscale-text-caption">
                ({current.length} requirements with history)
              </span>
            </h4>
            <div>
              {loading ? (
                <Loader2 className="mx-auto animate-spin text-secondary-text-label" size={20} />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Requirement</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Valid Until</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {current.map((c) => (
                      <TableRow key={c.requirement_code}>
                        <TableCell>
                          <code className="text-xs">{c.requirement_code}</code>
                          <Button
                            variant="link"
                            size="xs"
                            className="ml-xxs"
                            onClick={() =>
                              setRequirementFilter(
                                requirementFilter === c.requirement_code
                                  ? ''
                                  : c.requirement_code
                              )
                            }
                          >
                            filter
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Tag variant={EVENT_COLOR[c.event] || 'drafted'}>{c.event}</Tag>
                        </TableCell>
                        <TableCell className="text-xs">
                          {c.valid_until ? formatTimestamp(c.valid_until) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {current.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} className="py-sm text-center text-greyscale-text-caption">
                          No credentials recorded for this employee.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-2xl border border-greyscale-border-default bg-white p-md">
            <div className="mb-sm flex items-center justify-between gap-sm">
              <h4 className="text-sm font-semibold">Ledger History</h4>
              <div className="w-60">
                <SearchInput
                  placeholder="Filter by requirement code…"
                  value={requirementFilter}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setRequirementFilter(e.target.value)
                  }
                  onClear={() => setRequirementFilter('')}
                />
              </div>
            </div>
            <div>
              {loading ? (
                <Loader2 className="mx-auto animate-spin text-secondary-text-label" size={20} />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Seq</TableHead>
                      <TableHead>Requirement</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>When</TableHead>
                      <TableHead>Trigger</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledger.map((row) => (
                      <React.Fragment key={row.id}>
                        <TableRow>
                          <TableCell>{row.sequence_no}</TableCell>
                          <TableCell>
                            <code className="text-xs">{row.requirement_code}</code>
                          </TableCell>
                          <TableCell>
                            <Tag variant={EVENT_COLOR[row.event] || 'drafted'}>{row.event}</Tag>
                          </TableCell>
                          <TableCell className="text-xs">{row.source_type}</TableCell>
                          <TableCell className="text-xs">
                            {formatTimestamp(row.date_created)}
                          </TableCell>
                          <TableCell className="text-xs">{row.trigger_event}</TableCell>
                          <TableCell>
                            <Button
                              variant="link"
                              size="xs"
                              onClick={() =>
                                setShowSnapshot(showSnapshot === row.id ? null : row.id)
                              }
                            >
                              {showSnapshot === row.id ? 'hide' : 'snapshot'}
                            </Button>
                          </TableCell>
                        </TableRow>
                        {showSnapshot === row.id && (
                          <TableRow>
                            <TableCell colSpan={7} className="bg-greyscale-surface-subtle">
                              <pre className="m-none text-xxs">
                                {JSON.stringify(row.previous_credentials_snapshot, null, 2)}
                              </pre>
                              {(row.bsuid_at_event || row.msisdn_at_event) && (
                                <div className="mt-xxs text-xs">
                                  <strong>At event:</strong>{' '}
                                  {row.msisdn_at_event && `MSISDN ${row.msisdn_at_event}`}
                                  {row.msisdn_at_event && row.bsuid_at_event && ', '}
                                  {row.bsuid_at_event && `BSUID ${row.bsuid_at_event}`}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                    {ledger.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="py-sm text-center text-greyscale-text-caption">
                          No ledger entries.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeVerificationSection;
